import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { 
  FaServer, 
  FaPlus, 
  FaSpinner, 
  FaTrash, 
  FaPause, 
  FaPlay,
  FaDatabase,
  FaJava,
  FaNode,
  FaPython,
  FaRust,
  FaGlobe
} from 'react-icons/fa';
import { SiDotnet, SiApache, SiNginx } from 'react-icons/si';

function PterodactylPanel() {
  const [loading, setLoading] = useState(true);
  const [servers, setServers] = useState([]);
  const [serverTypes, setServerTypes] = useState({});
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAllServers, setShowAllServers] = useState(false);
  const [filterServerId, setFilterServerId] = useState('');
  const [newServer, setNewServer] = useState({
    name: '',
    serverType: 'nodejs',
    memory: 512,
    disk: 1024,
    cpu: 100,
    ownerId: '',
    startup: ''
  });

  const fetchServers = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/manage/pterodactyl', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'listServers',
          userId: localStorage.getItem('userId'),
          token: localStorage.getItem('token'),
          showAll: showAllServers,
          filterServerId: filterServerId || undefined
        }),
      });

      const data = await response.json();

      if (data.success) {
        setServers(data.servers);
      } else {
        setError(data.message || 'Failed to fetch servers');
      }
    } catch (err) {
      console.error('Error fetching servers:', err);
      setError('Failed to fetch servers from Pterodactyl panel');
    } finally {
      setLoading(false);
    }
  }, [showAllServers, filterServerId]);

  useEffect(() => {
    fetchCurrentUser();
    fetchServerTypes();
    fetchUsers();
    fetchServers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchServers();
  }, [showAllServers, filterServerId, fetchServers]);

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch('/api/manage/userUtils', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getUserData',
          userId: localStorage.getItem('userId'),
          token: localStorage.getItem('token')
        }),
      });

      const data = await response.json();
      if (data.success) {
        setCurrentUser(data.userData);
        // Set default owner to current user
        setNewServer(prev => ({ ...prev, ownerId: data.userData.id }));
      }
    } catch (err) {
      console.error('Error fetching current user:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/manage/pterodactyl', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getUsers',
          userId: localStorage.getItem('userId'),
          token: localStorage.getItem('token')
        }),
      });

      const data = await response.json();

      if (data.success) {
        setUsers(data.users);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchServerTypes = async () => {
    try {
      const response = await fetch('/api/manage/pterodactyl', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getServerTypes',
          userId: localStorage.getItem('userId'),
          token: localStorage.getItem('token')
        }),
      });

      const data = await response.json();

      if (data.success) {
        setServerTypes(data.serverTypes);
      } else {
        setError(data.message || 'Failed to fetch server types');
      }
    } catch (err) {
      console.error('Error fetching server types:', err);
      setError('Failed to fetch server types');
    }
  };

  const handleCreateServer = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/manage/pterodactyl', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'createServer',
          userId: localStorage.getItem('userId'),
          token: localStorage.getItem('token'),
          ...newServer
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Server created successfully!');
        setShowCreateForm(false);
        setNewServer({
          name: '',
          serverType: 'nodejs',
          memory: 512,
          disk: 1024,
          cpu: 100,
          ownerId: currentUser?.id || '',
          startup: ''
        });
        fetchServers();
      } else {
        setError(data.message || 'Failed to create server');
      }
    } catch (err) {
      console.error('Error creating server:', err);
      setError('Failed to create server');
    }
  };

  const handleDeleteServer = async (serverId) => {
    if (!window.confirm('Are you sure you want to delete this server? This action cannot be undone.')) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/manage/pterodactyl', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'deleteServer',
          userId: localStorage.getItem('userId'),
          token: localStorage.getItem('token'),
          serverId
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Server deleted successfully!');
        fetchServers();
      } else {
        setError(data.message || 'Failed to delete server');
      }
    } catch (err) {
      console.error('Error deleting server:', err);
      setError('Failed to delete server');
    }
  };

  const handleSuspendServer = async (serverId) => {
    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/manage/pterodactyl', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'suspendServer',
          userId: localStorage.getItem('userId'),
          token: localStorage.getItem('token'),
          serverId
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Server suspended successfully!');
        fetchServers();
      } else {
        setError(data.message || 'Failed to suspend server');
      }
    } catch (err) {
      console.error('Error suspending server:', err);
      setError('Failed to suspend server');
    }
  };

  const handleUnsuspendServer = async (serverId) => {
    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/manage/pterodactyl', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'unsuspendServer',
          userId: localStorage.getItem('userId'),
          token: localStorage.getItem('token'),
          serverId
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Server unsuspended successfully!');
        fetchServers();
      } else {
        setError(data.message || 'Failed to unsuspend server');
      }
    } catch (err) {
      console.error('Error unsuspending server:', err);
      setError('Failed to unsuspend server');
    }
  };

  const handleOpenConsole = async (serverId) => {
    try {
      const response = await fetch('/api/manage/pterodactyl', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getServerAccess',
          userId: localStorage.getItem('userId'),
          token: localStorage.getItem('token'),
          serverId
        }),
      });

      const data = await response.json();

      if (data.success && data.consoleUrl) {
        window.open(data.consoleUrl, '_blank');
      } else {
        setError(data.message || 'Failed to get console URL');
      }
    } catch (err) {
      console.error('Error opening console:', err);
      setError('Failed to open console');
    }
  };

  const handleOpenFileManager = async (serverId) => {
    try {
      const response = await fetch('/api/manage/pterodactyl', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getServerAccess',
          userId: localStorage.getItem('userId'),
          token: localStorage.getItem('token'),
          serverId
        }),
      });

      const data = await response.json();

      if (data.success && data.fileManagerUrl) {
        window.open(data.fileManagerUrl, '_blank');
      } else {
        setError(data.message || 'Failed to get file manager URL');
      }
    } catch (err) {
      console.error('Error opening file manager:', err);
      setError('Failed to open file manager');
    }
  };

  const getServerTypeIcon = (type) => {
    if (!type) return <FaServer />;
    
    switch (type) {
      case 'minio':
        return <FaDatabase />;
      case 'java':
        return <FaJava />;
      case 'csharp':
        return <SiDotnet />;
      case 'nodejs':
        return <FaNode />;
      case 'python':
        return <FaPython />;
      case 'rust':
        return <FaRust />;
      case 'apache2':
        return <SiApache />;
      case 'nginx':
        return <SiNginx />;
      default:
        return <FaServer />;
    }
  };

  return (
    <PanelContainer>
      <PanelHeader>
        <h2>Pterodactyl Panel Management</h2>
        <HeaderControls>
          {(currentUser?.role === 'admin' || currentUser?.role === 'root') && (
            <>
              <FilterGroup>
                <label>
                  <input
                    type="checkbox"
                    checked={showAllServers}
                    onChange={(e) => setShowAllServers(e.target.checked)}
                  />
                  Show All Servers
                </label>
              </FilterGroup>
              <FilterInput
                type="text"
                placeholder="Filter by Server ID"
                value={filterServerId}
                onChange={(e) => setFilterServerId(e.target.value)}
              />
            </>
          )}
          <CreateButton onClick={() => setShowCreateForm(!showCreateForm)}>
            <FaPlus /> Create Server
          </CreateButton>
        </HeaderControls>
      </PanelHeader>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      {showCreateForm && (
        <CreateForm onSubmit={handleCreateServer}>
          <h3>Create New Server</h3>
          <FormGroup>
            <label>Server Name</label>
            <input
              type="text"
              value={newServer.name}
              onChange={(e) => setNewServer({ ...newServer, name: e.target.value })}
              placeholder="Enter server name"
              required
            />
          </FormGroup>

          <FormGroup>
            <label>Server Type</label>
            <select
              value={newServer.serverType}
              onChange={(e) => setNewServer({ ...newServer, serverType: e.target.value })}
            >
              {Object.entries(serverTypes).map(([key, type]) => (
                <option key={key} value={key}>
                  {type.name}
                </option>
              ))}
            </select>
            {serverTypes[newServer.serverType] && (
              <TypeDescription>{serverTypes[newServer.serverType].description}</TypeDescription>
            )}
          </FormGroup>

          <FormGroup>
            <label>Startup Command (Optional)</label>
            <input
              type="text"
              value={newServer.startup}
              onChange={(e) => setNewServer({ ...newServer, startup: e.target.value })}
              placeholder="e.g., node index.js"
            />
            <TypeDescription>
              Leave empty to use default: {serverTypes[newServer.serverType]?.startup || 'N/A'}
            </TypeDescription>
          </FormGroup>

          {(currentUser?.role === 'admin' || currentUser?.role === 'root') && (
            <FormGroup>
              <label>Server Owner</label>
              <select
                value={newServer.ownerId}
                onChange={(e) => setNewServer({ ...newServer, ownerId: e.target.value })}
                required
              >
                <option value="">Select Owner</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.username} ({user.email}) - {user.role}
                  </option>
                ))}
              </select>
            </FormGroup>
          )}

          <FormRow>
            <FormGroup>
              <label>Memory (MB)</label>
              <input
                type="number"
                value={newServer.memory}
                onChange={(e) => setNewServer({ ...newServer, memory: parseInt(e.target.value) })}
                min="128"
                step="128"
              />
            </FormGroup>

            <FormGroup>
              <label>Disk (MB)</label>
              <input
                type="number"
                value={newServer.disk}
                onChange={(e) => setNewServer({ ...newServer, disk: parseInt(e.target.value) })}
                min="512"
                step="512"
              />
            </FormGroup>

            <FormGroup>
              <label>CPU (%)</label>
              <input
                type="number"
                value={newServer.cpu}
                onChange={(e) => setNewServer({ ...newServer, cpu: parseInt(e.target.value) })}
                min="10"
                max="400"
                step="10"
              />
            </FormGroup>
          </FormRow>

          <ButtonGroup>
            <SubmitButton type="submit">Create Server</SubmitButton>
            <CancelButton type="button" onClick={() => setShowCreateForm(false)}>
              Cancel
            </CancelButton>
          </ButtonGroup>
        </CreateForm>
      )}

      {loading ? (
        <LoadingWrapper>
          <FaSpinner className="spinner" />
          <p>Loading servers from Pterodactyl panel...</p>
        </LoadingWrapper>
      ) : (
        <ServersGrid>
          {servers.length === 0 ? (
            <EmptyState>
              <FaServer />
              <p>No servers found. Create your first server to get started!</p>
            </EmptyState>
          ) : (
            servers.map((server) => (
              <ServerCard key={server.id || server.attributes?.id}>
                <ServerHeader>
                  <ServerIcon>
                    {getServerTypeIcon(server.attributes?.container?.image?.split(':')[0] || null)}
                  </ServerIcon>
                  <ServerInfo>
                    <ServerName>{server.attributes?.name || 'Unnamed Server'}</ServerName>
                    <ServerId>ID: {server.attributes?.identifier || server.id}</ServerId>
                  </ServerInfo>
                  <ServerStatus suspended={server.attributes?.suspended}>
                    {server.attributes?.suspended ? 'Suspended' : 'Active'}
                  </ServerStatus>
                </ServerHeader>

                <ServerDetails>
                  <DetailItem>
                    <DetailLabel>Server ID:</DetailLabel>
                    <DetailValue>{server.attributes?.id || server.id}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Memory:</DetailLabel>
                    <DetailValue>{server.attributes?.limits?.memory || 0} MB</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Disk:</DetailLabel>
                    <DetailValue>{server.attributes?.limits?.disk || 0} MB</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>CPU:</DetailLabel>
                    <DetailValue>{server.attributes?.limits?.cpu || 0}%</DetailValue>
                  </DetailItem>
                </ServerDetails>

                <ServerActions>
                  <ActionButton 
                    onClick={() => handleOpenConsole(server.attributes?.id)}
                    title="Open Console"
                  >
                    🖥️ Console
                  </ActionButton>
                  <ActionButton 
                    onClick={() => handleOpenFileManager(server.attributes?.id)}
                    title="Open File Manager"
                  >
                    📁 Files
                  </ActionButton>
                  {server.attributes?.suspended ? (
                    <ActionButton 
                      primary 
                      onClick={() => handleUnsuspendServer(server.attributes.id)}
                    >
                      <FaPlay /> Unsuspend
                    </ActionButton>
                  ) : (
                    <ActionButton 
                      onClick={() => handleSuspendServer(server.attributes.id)}
                    >
                      <FaPause /> Suspend
                    </ActionButton>
                  )}
                  <ActionButton 
                    danger 
                    onClick={() => handleDeleteServer(server.attributes.id)}
                  >
                    <FaTrash /> Delete
                  </ActionButton>
                </ServerActions>
              </ServerCard>
            ))
          )}
        </ServersGrid>
      )}
    </PanelContainer>
  );
}

// Styled Components
const PanelContainer = styled.div`
  margin-bottom: 2rem;
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;

  h2 {
    margin: 0;
    color: var(--text-primary);
  }
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  
  label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--text-primary);
    cursor: pointer;
  }

  input[type="checkbox"] {
    cursor: pointer;
  }
`;

const FilterInput = styled.input`
  padding: 0.5rem 1rem;
  border: 1px solid var(--card-border);
  border-radius: var(--border-radius);
  background-color: var(--input-bg);
  color: var(--text-primary);
  
  &::placeholder {
    color: var(--text-secondary);
  }
`;

const CreateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: var(--accent-color);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: background-color var(--transition-speed) ease;

  &:hover {
    background-color: var(--accent-hover);
  }
`;

const CreateForm = styled.form`
  background-color: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: var(--border-radius);
  padding: 1.5rem;
  margin-bottom: 1.5rem;

  h3 {
    margin-top: 0;
    color: var(--text-primary);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    color: var(--text-primary);
    font-weight: 500;
  }

  input,
  select {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid var(--card-border);
    border-radius: var(--border-radius);
    background-color: var(--input-bg);
    color: var(--text-primary);
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const TypeDescription = styled.p`
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-style: italic;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const SubmitButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: var(--accent-color);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: background-color var(--transition-speed) ease;

  &:hover {
    background-color: var(--accent-hover);
  }
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: background-color var(--transition-speed) ease;

  &:hover {
    background-color: var(--bg-tertiary);
  }
`;

const ServersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
`;

const EmptyState = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: var(--text-secondary);

  svg {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
`;

const ServerCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  background-color: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: var(--border-radius);
  box-shadow: 0 2px 4px var(--card-shadow);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px var(--card-shadow);
  }
`;

const ServerHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--card-border);
`;

const ServerIcon = styled.div`
  font-size: 2rem;
  color: var(--accent-color);
  flex-shrink: 0;
`;

const ServerInfo = styled.div`
  flex: 1;
`;

const ServerName = styled.h3`
  margin: 0 0 0.25rem 0;
  color: var(--text-primary);
  font-size: 1.125rem;
`;

const ServerId = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-family: monospace;
`;

const ServerStatus = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => props.suspended 
    ? 'rgba(255, 152, 0, 0.1)' 
    : 'rgba(76, 175, 80, 0.1)'};
  color: ${props => props.suspended 
    ? 'var(--warning-color, #ff9800)' 
    : 'var(--success-color, #4caf50)'};
`;

const ServerDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DetailLabel = styled.span`
  color: var(--text-secondary);
  font-size: 0.875rem;
`;

const DetailValue = styled.span`
  color: var(--text-primary);
  font-weight: 500;
  font-family: monospace;
`;

const ServerActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: auto;
`;

const ActionButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: ${props => 
    props.danger ? 'rgba(244, 67, 54, 0.1)' : 
    props.primary ? 'var(--accent-color)' : 
    'var(--bg-secondary)'};
  color: ${props => 
    props.danger ? 'var(--error-color, #f44336)' : 
    props.primary ? 'white' : 
    'var(--text-primary)'};
  border: 1px solid ${props => 
    props.danger ? 'var(--error-color, #f44336)' : 
    props.primary ? 'var(--accent-color)' : 
    'var(--card-border)'};
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all var(--transition-speed) ease;
  font-size: 0.875rem;

  &:hover {
    background-color: ${props => 
      props.danger ? 'var(--error-color, #f44336)' : 
      props.primary ? 'var(--accent-hover)' : 
      'var(--bg-tertiary)'};
    color: ${props => (props.danger || props.primary) ? 'white' : 'var(--text-primary)'};
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: var(--text-secondary);

  .spinner {
    animation: spin 1s linear infinite;
    font-size: 2rem;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background-color: rgba(244, 67, 54, 0.1);
  color: var(--error-color, #f44336);
  padding: 1rem;
  border-radius: var(--border-radius);
  margin-bottom: 1.5rem;
  border: 1px solid var(--error-color, #f44336);
`;

const SuccessMessage = styled.div`
  background-color: rgba(76, 175, 80, 0.1);
  color: var(--success-color, #4caf50);
  padding: 1rem;
  border-radius: var(--border-radius);
  margin-bottom: 1.5rem;
  border: 1px solid var(--success-color, #4caf50);
`;

export default PterodactylPanel;
